import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff, Lock } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Logo } from "@/components/Logo";
import { EASE } from "@/components/Motion";
import { VisitorWelcome, knownVisitor, welcomeToast } from "@/components/VisitorWelcome";

export default function Gate() {
  const nav = useNavigate();
  const { refresh } = useSite() || {};
  const [code, setCode] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [shake, setShake] = useState(0);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const [ask, setAsk] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!code) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/gate/enter", { code });
      const dest = r.data?.redirect || "/family";
      setOpened(true);
      setCode("");
      const known = knownVisitor();
      if (known) {
        try { welcomeToast((await api.post("/visitor/email", known)).data); } catch (er) { /* ignore */ }
        await refresh?.();
        setTimeout(() => nav(dest), 1900);
      } else {
        setAsk(dest);
      }
    } catch (er) {
      setErr(errText(er)); setShake((s) => s + 1);
    }
    setBusy(false);
  };
  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-[#0B1D14] flex items-center justify-center px-6" data-testid="gate-page">
      <motion.div className="absolute inset-0" animate={{ filter: opened ? "grayscale(0) saturate(1.2)" : "grayscale(1) contrast(1.1)" }} transition={{ duration: 1.6 }}
        style={{ background: "radial-gradient(ellipse at 50% 40%, #2A5A3E 0%, #0F2A1D 45%, #070F0B 100%)" }} />
      <div className="vignette" />
      <AnimatePresence>
        {opened && (
          <>
            <motion.div className="absolute inset-y-0 left-0 w-1/2 z-30 bg-[#0F2A1D] border-r border-[#C9A24B]" initial={{ x: 0 }} animate={{ x: "-100%" }} transition={{ duration: 1.4, ease: EASE, delay: 0.2 }} />
            <motion.div className="absolute inset-y-0 right-0 w-1/2 z-30 bg-[#0F2A1D] border-l border-[#C9A24B]" initial={{ x: 0 }} animate={{ x: "100%" }} transition={{ duration: 1.4, ease: EASE, delay: 0.2 }} />
            <motion.div className="absolute inset-0 z-20 grid place-items-center px-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} data-testid="gate-success">
              {ask ? <VisitorWelcome dest={ask} onDone={async () => { await refresh?.(); nav(ask); }} /> : (
                <div className="text-center"><Logo size={64} className="mx-auto" /><p className="mt-6 font-display text-4xl text-[#F6EFE0]">Welcome home</p></div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {!opened && (
        <motion.div className="relative z-10 w-full max-w-md text-center" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}>
          <div className="mb-6 text-left sm:-ml-16"><Link to="/" className="inline-flex items-center gap-2 text-sm text-[#E4C87F] hover:text-[#F6EFE0] transition-colors" data-testid="gate-back-home"><ArrowLeft size={16} /> Back to Homepage</Link></div>
          <motion.div className="mx-auto grid place-items-center w-24 h-24 rounded-full border border-[#C9A24B] text-[#C9A24B] shadow-[0_0_60px_rgba(201,162,75,.25)]" animate={{ boxShadow: ["0 0 40px rgba(201,162,75,.15)", "0 0 70px rgba(201,162,75,.35)", "0 0 40px rgba(201,162,75,.15)"] }} transition={{ duration: 4, repeat: Infinity }}>
            <Lock size={30} />
          </motion.div>
          <h1 className="mt-8 font-display text-4xl sm:text-5xl text-[#F6EFE0]">Family Members Only</h1>
          <p className="mt-4 text-[#F6EFE0]/85" data-testid="gate-instruction">Enter family member code to view details or history</p>
          <form onSubmit={submit} key={shake} className={`mt-10 ${shake ? "shake" : ""}`} data-testid="gate-form">
            <div className="relative">
              <input type={show ? "text" : "password"} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" spellCheck="false" placeholder="Your code"
                className="field !rounded-full !py-4 !pl-6 !pr-14 text-center tracking-[0.3em] text-lg" data-testid="gate-code-input" aria-label="Family member code" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#C9A24B]" aria-label={show ? "Hide code" : "Show code"} data-testid="gate-toggle-visibility">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            <button className="btn-gold mt-5 w-full justify-center" disabled={busy || !code} data-testid="gate-enter-btn">{busy ? "Checking..." : "Enter"}</button>
          </form>
          <AnimatePresence>{err && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-5 text-sm text-[#F2B8A0]" role="alert" data-testid="gate-error">{err}</motion.p>}</AnimatePresence>
          <p className="mt-10 text-xs text-[#F6EFE0]/65">Codes are case-sensitive. After 5 wrong tries the gate rests for 15 minutes.</p>
        </motion.div>
      )}
    </div>
  );
}
